/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import type { BasicFunction } from '../../helpers/jsHelpers/types';

import { fireAndForget } from '../../helpers/jsHelpers/promise';
import { downcaseFirstLetter } from '../../helpers/jsHelpers/string';
import Localizer from '../../helpers/localization';
import ProfilesManager from '../../helpers/profiles/profilesManager';
import { PROFILE_AVATAR_SIZE } from '../../helpers/ui/constants';
import { getImageUrl } from '../../redux/netgemApi/actions/v8/metadataImage';
import { PictoEdit, PictoKid, PictoLock, PictoUser } from '../pictos/Element';
import ProfileTypeIndicator, { ProfileType } from './ProfileTypeIndicator';
import { AvatarSize, DisplayNamePlacement, EditMode } from './types';
import './Avatar.css';

type AvatarPropType = {
  +'aria-label'?: string,
  +avatarId: string | null,
  +cutIfTooLong?: boolean,
  +displayName?: DisplayNamePlacement,
  +editMode?: EditMode,
  +isDisabled?: boolean,
  +isKid?: boolean,
  +isPrivate?: boolean,
  +isSelected?: boolean,
  +isUnavailable?: boolean,
  +name?: string,
  +onClick?: (avatarId: string | null) => void,
  +onLoaded?: BasicFunction,
  +profileId?: string | null,
  +size?: AvatarSize,
  +tabIndex?: number,
};

const placementToClass = (displayName: DisplayNamePlacement): string | null => {
  switch (displayName) {
    case DisplayNamePlacement.Hidden:
      return null;
    case DisplayNamePlacement.Bottom:
      return 'displayNameBottom';
    case DisplayNamePlacement.Right:
      return 'displayNameRight';

    // No default
  }

  return null;
};

const Avatar = (avatarProps: AvatarPropType): React.Node => {
  const {
    'aria-label': ariaLabel,
    avatarId,
    cutIfTooLong = false,
    displayName = DisplayNamePlacement.Hidden,
    editMode = EditMode.Hidden,
    isDisabled = false,
    isKid = false,
    isPrivate = false,
    isSelected = false,
    isUnavailable = false,
    name = '',
    onClick,
    onLoaded,
    profileId = null,
    size = AvatarSize.Medium,
    tabIndex = 0,
  } = avatarProps;

  const dispatch = useDispatch();

  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  const index = useMemo<number>(() => ProfilesManager.getProfileIndex(profileId));

  // Loads avatar image when mounting
  useEffect(() => {
    const loadAvatar = async (assetId: string) => {
      const sizePx = PROFILE_AVATAR_SIZE[size as string];
      const newAvatarUrl = await dispatch(getImageUrl({ assetId, height: sizePx, width: sizePx }));
      setAvatarUrl(newAvatarUrl);

      if (onLoaded) {
        // Notify that image has been loaded
        onLoaded();
      }
    };

    if (avatarId === null) {
      if (onLoaded) {
        // Notify that image has been loaded
        onLoaded();
      }

      return;
    }

    fireAndForget(loadAvatar(avatarId), 'Avatar.loadAvatar');
  }, [avatarId, onLoaded, size]);

  const initial = useMemo(() => {
    const [result] = name.trim();

    if (!result) {
      return '';
    }

    if (/\w/iu.test(result)) {
      return result.toUpperCase();
    }

    return result;
  }, [name]);

  const handleOnClick = useCallback(() => {
    if (onClick) {
      onClick(avatarId);
    }
  }, [avatarId, onClick]);

  const renderContent = useCallback(() => {
    if (avatarId !== null) {
      if (avatarUrl !== null) {
        // Display avatar image
        return <img alt={Localizer.localize('accessibility.profiles.avatar_for_profile', { nickname: name })} aria-hidden draggable='false' src={avatarUrl} />;
      }

      // Image not loaded yet
      return null;
    }

    if (initial !== undefined && initial !== '') {
      // Display initial of profile name
      return <div className='profileInitial'>{initial}</div>;
    }

    // Display empty circle with initial placeholder
    return (
      <div className='noAvatar'>
        <div className='profileInitialPlaceholder'>ABC</div>
      </div>
    );
  }, [avatarId, avatarUrl, name]);

  return (
    <button
      aria-label={ariaLabel}
      className={clsx(
        'avatarContainer',
        (size as string).toLowerCase(),
        isSelected && 'selected',
        downcaseFirstLetter(editMode as string),
        placementToClass(displayName),
        isUnavailable && 'unavailable',
        cutIfTooLong && 'cutIfTooLong',
      )}
      disabled={isDisabled || isUnavailable}
      onClick={handleOnClick}
      tabIndex={tabIndex}
      type='button'
    >
      <div className='avatarImageWrapper'>
        <div className={clsx('avatarImage', avatarId === null && `profile${index}`)}>
          {renderContent()}
          <PictoEdit aria-hidden className='editPicto' />
          <PictoUser aria-hidden className='userPicto' />
        </div>
        {isPrivate && displayName !== DisplayNamePlacement.Right ? <PictoLock aria-hidden className='typePicto' /> : null}
        {isKid && displayName !== DisplayNamePlacement.Right ? <PictoKid aria-hidden className='typePicto' /> : null}
      </div>
      <div className='avatarNameWrapper'>
        {displayName !== DisplayNamePlacement.Hidden ? <div className='avatarName'>{name}</div> : null}
        {displayName === DisplayNamePlacement.Right && (isKid || isPrivate) ? <ProfileTypeIndicator profileType={isKid ? ProfileType.Kid : ProfileType.Private} /> : null}
      </div>
    </button>
  );
};

export default Avatar;
